import Konva from 'konva'
import QrcodeVue from 'qrcode.vue'
import { createApp, nextTick } from 'vue'
import qrFooterLogo from '@/assets/images/qr-footer-logo.svg'

function generateQRDataUrl(value: string, size: number): Promise<string> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    document.body.appendChild(container)

    const app = createApp(QrcodeVue, {
      value,
      size,
      level: 'M',
      renderAs: 'canvas'
    })
    app.mount(container)

    nextTick(() => {
      const canvas = container.querySelector('canvas')
      const dataUrl = canvas ? canvas.toDataURL('image/png') : ''
      app.unmount()
      document.body.removeChild(container)
      resolve(dataUrl)
    })
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function parseHex(color?: string): number {
  const match = /^#?([0-9a-f]{6})$/i.exec((color || '').trim())
  return match ? parseInt(match[1], 16) : 0x14919B
}

function rgba(n: number, alpha: number): string {
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

function luminance(n: number): number {
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
}

export const QR_IMAGE_STYLES = ['minimal', 'professional', 'fun', 'colorful'] as const
export type QRImageStyle = typeof QR_IMAGE_STYLES[number]

export interface QRExportOptions {
  qrUrl: string
  logoSrc: string
  customerName?: string
  externalID?: string
  examDate?: string
  brandColor?: string
  style?: string
}

const WIDTH = 540
const FONT = 'Tajawal-Medium, Tajawal, Arial, sans-serif'

interface Ctx {
  layer: Konva.Layer
  background: Konva.Rect
  brandHex: number
  brand: string
  logo: HTMLImageElement
  footer: HTMLImageElement
  qr: HTMLImageElement
  options: QRExportOptions
  meta: string
  text: (value: string, y: number, size: number, opts?: Record<string, any>) => Konva.Text
}

function logoSize(ctx: Ctx, maxW: number, maxH: number): [number, number] {
  const w0 = ctx.logo.naturalWidth || maxW
  const h0 = ctx.logo.naturalHeight || maxH
  const scale = Math.min(maxW / w0, maxH / h0)
  return [w0 * scale, h0 * scale]
}

function addLogo(ctx: Ctx, y: number, maxW: number, maxH: number): number {
  const [w, h] = logoSize(ctx, maxW, maxH)
  ctx.layer.add(new Konva.Image({ x: (WIDTH - w) / 2, y, width: w, height: h, image: ctx.logo }))
  return h
}

function addFooter(ctx: Ctx, y: number, width: number, opacity = 1): number {
  const h = ctx.footer.naturalHeight * (width / ctx.footer.naturalWidth)
  ctx.layer.add(new Konva.Image({ x: (WIDTH - width) / 2, y, width, height: h, image: ctx.footer, opacity }))
  return h
}

function addQR(ctx: Ctx, y: number, size: number) {
  ctx.layer.add(new Konva.Image({ x: (WIDTH - size) / 2, y, width: size, height: size, image: ctx.qr }))
}

function addDiamond(ctx: Ctx, y: number, color: string) {
  const cx = WIDTH / 2
  ctx.layer.add(new Konva.Line({ points: [cx - 64, y, cx - 12, y], stroke: color, strokeWidth: 1.2, opacity: 0.6 }))
  ctx.layer.add(new Konva.Line({ points: [cx + 12, y, cx + 64, y], stroke: color, strokeWidth: 1.2, opacity: 0.6 }))
  ctx.layer.add(new Konva.Rect({ x: cx, y: y - 4, width: 8, height: 8, offsetX: 4, rotation: 45, fill: color }))
}

function addNameAndMeta(ctx: Ctx, y: number, nameColor: string, metaColor: string): number {
  if (ctx.options.customerName) {
    const name = ctx.text(ctx.options.customerName, y, 26, { fontStyle: 'bold', fill: nameColor })
    y += name.height() + 10
  }
  if (ctx.meta) {
    const metaNode = ctx.text(ctx.meta, y, 15, { fill: metaColor })
    y += metaNode.height()
  }
  return y
}

function renderMinimal(ctx: Ctx): number {
  ctx.background.fill('#ffffff')
  let y = 64
  y += addLogo(ctx, y, 150, 66) + 44
  const QR = 380
  addQR(ctx, y, QR)
  y += QR + 40
  y = addNameAndMeta(ctx, y, '#111111', '#8a8a8a') + 40
  ctx.layer.add(new Konva.Line({ points: [WIDTH / 2 - 40, y, WIDTH / 2 + 40, y], stroke: '#d9d9d9', strokeWidth: 1 }))
  y += 30
  const ar = ctx.text('امسح الرمز لرؤية نتائج فحصك', y, 19, { fill: '#333333', direction: 'rtl' })
  y += ar.height() + 6
  const en = ctx.text('Scan the code to view your exam results', y, 13, { fill: '#9a9a9a' })
  y += en.height() + 44
  y += addFooter(ctx, y, 90, 0.55) + 40
  return y
}

function renderProfessional(ctx: Ctx): number {
  const { layer, brand, brandHex } = ctx
  const CARD_X = 36
  const CARD_TOP = 104
  const QR = 296
  const PAD = 18
  ctx.background.fill(rgba(brandHex, 0.06))
  layer.add(new Konva.Rect({ x: 0, y: 0, width: WIDTH, height: 230, fill: brand }))
  const card = new Konva.Rect({
    x: CARD_X, y: CARD_TOP, width: WIDTH - CARD_X * 2, height: 100, fill: '#ffffff', cornerRadius: 28,
    shadowColor: '#000000', shadowBlur: 30, shadowOpacity: 0.12, shadowOffsetY: 10
  })
  layer.add(card)
  let y = CARD_TOP + 34
  y += addLogo(ctx, y, 170, 84) + 20
  addDiamond(ctx, y, brand)
  y += 22
  const title = ctx.text('نتائج فحص البصر', y, 28, { fontStyle: 'bold', direction: 'rtl' })
  y += title.height() + 8
  const subtitle = ctx.text('EYE EXAMINATION RESULTS', y, 12, { fill: '#6b6b6b', letterSpacing: 4 })
  y += subtitle.height() + 26
  const frame = QR + PAD * 2
  layer.add(new Konva.Rect({ x: (WIDTH - frame) / 2, y, width: frame, height: frame, cornerRadius: 24, stroke: brand, strokeWidth: 2.5 }))
  addQR(ctx, y + PAD, QR)
  y += frame + 26
  y = addNameAndMeta(ctx, y, '#1f1f1f', '#6b6b6b') + 34
  card.height(y - CARD_TOP)
  y += 30
  const ar = ctx.text('امسح الرمز لرؤية نتائج فحصك', y, 20, { fill: '#333333', direction: 'rtl' })
  y += ar.height() + 8
  const en = ctx.text('Scan the code to view your exam results', y, 14, { fill: '#777777' })
  y += en.height() + 34
  y += addFooter(ctx, y, 110) + 32
  return y
}

function renderFun(ctx: Ctx): number {
  const { layer, brand, brandHex } = ctx
  const palette = [brand, '#FFC93C', '#FF6B9A', '#7C5CFF', '#3DD6A3']
  ctx.background.fill(rgba(brandHex, 0.1))
  const confetti: [number, number, number, number, 'c' | 's' | 'r'][] = [
    [38, 40, 14, 1, 'c'], [500, 70, 18, 2, 's'], [470, 190, 9, 3, 'c'], [60, 220, 16, 4, 's'],
    [26, 520, 10, 2, 'r'], [516, 470, 14, 1, 'c'], [30, 760, 18, 3, 's'], [512, 720, 10, 4, 'r'],
    [44, 980, 12, 1, 'c'], [496, 960, 18, 2, 's'], [270, 28, 8, 3, 'c'], [150, 120, 7, 4, 'c'], [400, 130, 7, 1, 'r']
  ]
  for (const [x, y, r, c, kind] of confetti) {
    const fill = palette[c]
    if (kind === 'c') layer.add(new Konva.Circle({ x, y, radius: r, fill, opacity: 0.85 }))
    else if (kind === 's') layer.add(new Konva.Star({ x, y, numPoints: 5, innerRadius: r * 0.45, outerRadius: r, fill, opacity: 0.85, rotation: x % 40 }))
    else layer.add(new Konva.Rect({ x, y, width: r * 2.2, height: r * 0.9, fill, cornerRadius: r, rotation: (y % 60) - 30, opacity: 0.85 }))
  }

  let y = 70
  y += addLogo(ctx, y, 150, 70) + 26

  // eyeglasses doodle
  const cx = WIDTH / 2
  const gy = y + 22
  layer.add(new Konva.Circle({ x: cx - 34, y: gy, radius: 22, stroke: '#222222', strokeWidth: 4, fill: '#FFC93C' }))
  layer.add(new Konva.Circle({ x: cx + 34, y: gy, radius: 22, stroke: '#222222', strokeWidth: 4, fill: '#FFC93C' }))
  layer.add(new Konva.Line({ points: [cx - 12, gy - 4, cx, gy - 12, cx + 12, gy - 4], stroke: '#222222', strokeWidth: 4, tension: 0.5, lineCap: 'round' }))
  layer.add(new Konva.Circle({ x: cx - 30, y: gy + 2, radius: 7, fill: '#222222' }))
  layer.add(new Konva.Circle({ x: cx + 38, y: gy + 2, radius: 7, fill: '#222222' }))
  y += 64

  const title = ctx.text('نتائج فحصك جاهزة!', y, 32, { fontStyle: 'bold', direction: 'rtl', fill: '#222222' })
  y += title.height() + 6
  const sub = ctx.text('YOUR RESULTS ARE READY!', y, 14, { fill: brand, fontStyle: 'bold', letterSpacing: 3 })
  y += sub.height() + 30

  const QR = 300
  const PAD = 24
  const frame = QR + PAD * 2
  const fx = (WIDTH - frame) / 2
  layer.add(new Konva.Rect({ x: fx + 8, y: y + 10, width: frame, height: frame, cornerRadius: 34, fill: '#222222' }))
  layer.add(new Konva.Rect({ x: fx, y, width: frame, height: frame, cornerRadius: 34, fill: '#ffffff', stroke: '#222222', strokeWidth: 4 }))
  addQR(ctx, y + PAD, QR)

  const badge = new Konva.Group({ x: fx + frame - 40, y: y - 16, rotation: 8 })
  const badgeText = new Konva.Text({ text: 'امسحني • SCAN ME', fontSize: 15, fontFamily: FONT, fontStyle: 'bold', fill: '#222222', padding: 10 })
  badge.add(new Konva.Rect({ width: badgeText.width(), height: badgeText.height(), fill: '#FFC93C', cornerRadius: badgeText.height() / 2, stroke: '#222222', strokeWidth: 3 }))
  badge.add(badgeText)
  badge.offsetX(badgeText.width() / 2)
  layer.add(badge)
  y += frame + 40

  y = addNameAndMeta(ctx, y, '#222222', '#555555') + 30
  const ar = ctx.text('امسح الرمز وشوف نتائج فحصك', y, 20, { fill: '#222222', direction: 'rtl' })
  y += ar.height() + 6
  const en = ctx.text('Scan the code to see your exam results', y, 14, { fill: '#555555' })
  y += en.height() + 38
  y += addFooter(ctx, y, 100) + 36
  return y
}

function renderColorful(ctx: Ctx): number {
  const { layer, brand, brandHex } = ctx
  const light = luminance(brandHex) > 0.72
  const onBg = light ? '#1f1f1f' : '#ffffff'
  ctx.background.setAttrs({
    fillLinearGradientStartPoint: { x: 0, y: 0 },
    fillLinearGradientEndPoint: { x: WIDTH, y: 1000 },
    fillLinearGradientColorStops: [0, brand, 0.55, '#7C5CFF', 1, '#FF6B9A']
  })
  const bubbles: [number, number, number][] = [[480, 60, 120], [40, 330, 90], [520, 640, 70], [60, 900, 130], [300, 1080, 60]]
  for (const [x, y, r] of bubbles) layer.add(new Konva.Circle({ x, y, radius: r, fill: '#ffffff', opacity: 0.12 }))

  let y = 56
  const logoBoxW = 220
  const logoBoxH = 104
  layer.add(new Konva.Rect({ x: (WIDTH - logoBoxW) / 2, y, width: logoBoxW, height: logoBoxH, fill: '#ffffff', cornerRadius: 26, shadowColor: '#000000', shadowBlur: 20, shadowOpacity: 0.15, shadowOffsetY: 6 }))
  addLogo(ctx, y + (logoBoxH - logoSize(ctx, 160, 72)[1]) / 2, 160, 72)
  y += logoBoxH + 30

  const title = ctx.text('نتائج فحص البصر', y, 32, { fontStyle: 'bold', direction: 'rtl', fill: onBg })
  y += title.height() + 6
  const sub = ctx.text('EYE EXAMINATION RESULTS', y, 13, { fill: onBg, opacity: 0.85, letterSpacing: 4 })
  y += sub.height() + 30

  const QR = 300
  const PAD = 22
  const CARD_X = 52
  const cardTop = y
  const card = new Konva.Rect({ x: CARD_X, y, width: WIDTH - CARD_X * 2, height: 100, fill: '#ffffff', cornerRadius: 32, shadowColor: '#000000', shadowBlur: 30, shadowOpacity: 0.2, shadowOffsetY: 12 })
  layer.add(card)
  y += PAD + 6
  addQR(ctx, y, QR)
  y += QR + 22
  layer.add(new Konva.Rect({
    x: WIDTH / 2 - 60, y, width: 120, height: 5, cornerRadius: 3,
    fillLinearGradientStartPoint: { x: 0, y: 0 }, fillLinearGradientEndPoint: { x: 120, y: 0 },
    fillLinearGradientColorStops: [0, brand, 0.5, '#7C5CFF', 1, '#FF6B9A']
  }))
  y += 22
  y = addNameAndMeta(ctx, y, '#1f1f1f', '#6b6b6b') + 28
  card.height(y - cardTop)
  y += 32

  const ar = ctx.text('امسح الرمز لرؤية نتائج فحصك', y, 21, { fill: onBg, fontStyle: 'bold', direction: 'rtl' })
  y += ar.height() + 6
  const en = ctx.text('Scan the code to view your exam results', y, 14, { fill: onBg, opacity: 0.85 })
  y += en.height() + 34

  const pillW = 150
  const fh = ctx.footer.naturalHeight * (110 / ctx.footer.naturalWidth)
  layer.add(new Konva.Rect({ x: (WIDTH - pillW) / 2, y, width: pillW, height: fh + 22, fill: '#ffffff', cornerRadius: (fh + 22) / 2, opacity: 0.95 }))
  addFooter(ctx, y + 11, 110)
  y += fh + 22 + 36
  return y
}

const RENDERERS: Record<QRImageStyle, (ctx: Ctx) => number> = {
  minimal: renderMinimal,
  professional: renderProfessional,
  fun: renderFun,
  colorful: renderColorful
}

export async function renderQRCard(options: QRExportOptions): Promise<string> {
  const style: QRImageStyle = (QR_IMAGE_STYLES as readonly string[]).includes(options.style || '')
    ? options.style as QRImageStyle
    : 'professional'
  const brandHex = parseHex(options.brandColor)

  try {
    await document.fonts?.load('24px Tajawal-Medium')
  } catch {
    // fall back to the next font in FONT
  }

  const [logo, footer, qr] = await Promise.all([
    loadImage(options.logoSrc),
    loadImage(qrFooterLogo),
    generateQRDataUrl(options.qrUrl, 760).then(loadImage)
  ])

  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  document.body.appendChild(container)

  const stage = new Konva.Stage({ container, width: WIDTH, height: 1400 })
  const layer = new Konva.Layer()
  stage.add(layer)
  const background = new Konva.Rect({ x: 0, y: 0, width: WIDTH, height: 1400 })
  layer.add(background)

  const ctx: Ctx = {
    layer,
    background,
    brandHex,
    brand: rgba(brandHex, 1),
    logo,
    footer,
    qr,
    options,
    meta: [options.externalID ? `Exam No. ${options.externalID}` : '', options.examDate || ''].filter(Boolean).join('   •   '),
    text: (value, y, size, opts = {}) => {
      const node = new Konva.Text({
        x: 40, y, width: WIDTH - 80, text: value, fontSize: size, fontFamily: FONT, fill: '#1f1f1f', align: 'center', ...opts
      })
      layer.add(node)
      return node
    }
  }

  const height = RENDERERS[style](ctx)
  stage.height(height)
  background.height(height)
  layer.draw()

  const dataUrl = stage.toDataURL({ pixelRatio: 2 })
  stage.destroy()
  document.body.removeChild(container)
  return dataUrl
}

export async function exportQRCode(options: QRExportOptions): Promise<void> {
  const dataUrl = await renderQRCard(options)
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = `QR-${options.customerName || 'exam'}-${options.externalID || 'unknown'}.png`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
